import { usePresence } from "@tamagui/use-presence";
import {
  isWeb,
  useEvent,
  useIsomorphicLayoutEffect,
  useSafeRef
} from "@tamagui/web";
import { useEffect, useMemo } from "react";
import { Animated } from "react-native";
const animatedStyleKey = {
  transform: true,
  opacity: true
};
const colorStyleKey = {
  backgroundColor: true,
  color: true,
  borderColor: true,
  borderLeftColor: true,
  borderRightColor: true,
  borderTopColor: true,
  borderBottomColor: true
};
const costlyToAnimateStyleKey = {
  borderRadius: true,
  borderTopLeftRadius: true,
  borderTopRightRadius: true,
  borderBottomLeftRadius: true,
  borderBottomRightRadius: true,
  borderWidth: true,
  borderLeftWidth: true,
  borderRightWidth: true,
  borderTopWidth: true,
  borderBottomWidth: true,
  ...colorStyleKey
  // TODO for other keys like height or width, it's better to not add them here till layout animations are ready
};
const AnimatedView = Animated.View;
const AnimatedText = Animated.Text;
function useAnimatedNumber(initial) {
  const state = useSafeRef(
    null
  );
  if (!state.current) {
    state.current = {
      composite: null,
      val: new Animated.Value(initial),
      strategy: { type: "spring" }
    };
  }
  return {
    getInstance() {
      return state.current.val;
    },
    getValue() {
      return state.current.val["_value"];
    },
    stop() {
      var _a;
      (_a = state.current.composite) == null ? void 0 : _a.stop();
      state.current.composite = null;
    },
    setValue(next, { type, ...config } = { type: "spring" }, onFinish) {
      var _a, _b;
      const val = state.current.val;
      const handleFinish = onFinish ? ({ finished }) => finished ? onFinish() : null : void 0;
      if (type === "direct") {
        val.setValue(next);
      } else if (type === "spring") {
        (_a = state.current.composite) == null ? void 0 : _a.stop();
        const composite = Animated.spring(val, {
          ...config,
          toValue: next,
          useNativeDriver: !isWeb
        });
        composite.start(handleFinish);
        state.current.composite = composite;
      } else {
        (_b = state.current.composite) == null ? void 0 : _b.stop();
        const composite = Animated.timing(val, {
          ...config,
          toValue: next,
          useNativeDriver: !isWeb
        });
        composite.start(handleFinish);
        state.current.composite = composite;
      }
    }
  };
}
function useAnimatedNumberReaction({
  value
}, onValue) {
  const onChange = useEvent((current) => {
    onValue(current.value);
  });
  useEffect(() => {
    const id = value.getInstance().addListener(onChange);
    return () => {
      value.getInstance().removeListener(id);
    };
  }, [value, onChange]);
}
function useAnimatedNumberStyle(value, getStyle) {
  return getStyle(value.getInstance());
}
function createAnimations(animations) {
  AnimatedView["displayName"] = "AnimatedView";
  AnimatedText["displayName"] = "AnimatedText";
  return {
    isReactNative: true,
    animations,
    View: AnimatedView,
    Text: AnimatedText,
    useAnimatedNumber,
    useAnimatedNumberReaction,
    useAnimatedNumberStyle,
    usePresence,
    useAnimations: ({ props, onDidAnimate, style, state, presence }) => {
      const isExiting = (presence == null ? void 0 : presence[0]) === false;
      const sendExitComplete = presence == null ? void 0 : presence[1];
      const animateStyles = useSafeRef({});
      const animatedTranforms = useSafeRef([]);
      const animationsState = useSafeRef(
        /* @__PURE__ */ new WeakMap()
      );
      const animateOnly = props.animateOnly || [];
      const args = [style, state, isExiting, !!onDidAnimate];
      const isThereNoNativeStyleKeys = useMemo(() => {
        if (isWeb)
          return true;
        return Object.keys(style).some((key) => {
          if (animateOnly.length) {
            return !animatedStyleKey[key] && animateOnly.indexOf(key) === -1;
          } else {
            return !animatedStyleKey[key];
          }
        });
      }, args);
      const res = useMemo(() => {
        var _a;
        const runners = [];
        const completions = [];
        const nonAnimatedStyle = {};
        for (const key in style) {
          const val = style[key];
          if (!animatedStyleKey[key] && !costlyToAnimateStyleKey[key]) {
            nonAnimatedStyle[key] = val;
            continue;
          }
          if (animateOnly.length && animateOnly.indexOf(key) === -1) {
            nonAnimatedStyle[key] = val;
            continue;
          }
          if (key !== "transform") {
            animateStyles.current[key] = update(key, animateStyles.current[key], val);
            continue;
          }
          if (!val)
            continue;
          for (const [index, transform] of val.entries()) {
            if (!transform)
              continue;
            const tkey = Object.keys(transform)[0];
            const currentTransform = (_a = animatedTranforms.current[index]) == null ? void 0 : _a[tkey];
            animatedTranforms.current[index] = {
              [tkey]: update(tkey, currentTransform, transform[tkey])
            };
            animatedTranforms.current = [...animatedTranforms.current];
          }
        }
        const animatedStyle = {
          ...Object.fromEntries(
            Object.entries(animateStyles.current).map(([k, v]) => {
              var _a2;
              return [
                k,
                ((_a2 = animationsState.current.get(v)) == null ? void 0 : _a2.interpolation) || v
              ];
            })
          ),
          transform: animatedTranforms.current.map((r) => {
            var _a2;
            const key = Object.keys(r)[0];
            const val = ((_a2 = animationsState.current.get(r[key])) == null ? void 0 : _a2.interpolation) || r[key];
            return { [key]: val };
          })
        };
        return {
          runners,
          completions,
          style: [nonAnimatedStyle, animatedStyle]
        };
        function update(key, animated, valIn) {
          const isColorStyleKey = colorStyleKey[key];
          const [val, type] = isColorStyleKey ? [0, void 0] : getValue(valIn);
          let animateToValue = val;
          const value = animated || new Animated.Value(val);
          const curInterpolation = animationsState.current.get(value);
          let interpolateArgs;
          if (type) {
            interpolateArgs = getInterpolated(
              (curInterpolation == null ? void 0 : curInterpolation.current) ?? value["_value"],
              val,
              type
            );
            animationsState.current.set(value, {
              interpolation: value.interpolate(interpolateArgs),
              current: val
            });
          }
          if (isColorStyleKey) {
            animateToValue = (curInterpolation == null ? void 0 : curInterpolation.animateToValue) ? 0 : 1;
            interpolateArgs = getColorInterpolated(
              curInterpolation == null ? void 0 : curInterpolation.current,
              // valIn is the next color
              valIn,
              animateToValue
            );
            animationsState.current.set(value, {
              current: valIn,
              interpolation: value.interpolate(interpolateArgs),
              animateToValue: (curInterpolation == null ? void 0 : curInterpolation.animateToValue) ? 0 : 1
            });
          }
          if (value) {
            const animationConfig = getAnimationConfig(key, animations, props.animation);
            let resolve;
            const promise = new Promise((res2) => {
              resolve = res2;
            });
            completions.push(promise);
            runners.push(() => {
              value.stopAnimation();
              function getAnimation() {
                return Animated[animationConfig.type || "spring"](value, {
                  toValue: animateToValue,
                  useNativeDriver: !isWeb && !isThereNoNativeStyleKeys,
                  ...animationConfig
                });
              }
              const animation = animationConfig.delay ? Animated.sequence([
                Animated.delay(animationConfig.delay),
                getAnimation()
              ]) : getAnimation();
              animation.start(({ finished }) => {
                if (finished) {
                  resolve();
                }
              });
            });
          }
          if (process.env.NODE_ENV === "development") {
            if (props["debug"] === "verbose") {
              console.log(" \u{1F4A0} animate", key, `from ${value["_value"]} to`, valIn, `(${val})`, "type", type, "interpolate", interpolateArgs);
            }
          }
          return value;
        }
      }, args);
      useIsomorphicLayoutEffect(() => {
        res.runners.forEach((r) => r());
        let cancel = false;
        Promise.all(res.completions).then(() => {
          if (cancel)
            return;
          onDidAnimate == null ? void 0 : onDidAnimate();
          if (isExiting) {
            sendExitComplete == null ? void 0 : sendExitComplete();
          }
        });
        return () => {
          cancel = true;
        };
      }, args);
      if (process.env.NODE_ENV === "development") {
        if (props["debug"] === "verbose") {
          console.log(`Returning animated`, res);
        }
      }
      return res;
    }
  };
}
function getColorInterpolated(currentColor, nextColor, animateToValue) {
  const inputRange = [0, 1];
  const outputRange = [currentColor ? currentColor : nextColor, nextColor];
  if (animateToValue === 0) {
    outputRange.reverse();
  }
  return {
    inputRange,
    outputRange
  };
}
function getInterpolated(current, next, postfix = "deg") {
  if (next === current) {
    current = next - 1e-9;
  }
  const inputRange = [current, next];
  const outputRange = [`${current}${postfix}`, `${next}${postfix}`];
  if (next < current) {
    inputRange.reverse();
    outputRange.reverse();
  }
  return {
    inputRange,
    outputRange
  };
}
function getAnimationConfig(key, animations, animation) {
  var _a, _b;
  if (typeof animation === "string") {
    return animations[animation];
  }
  let type = "";
  let extraConf;
  const shortKey = transformShorthands[key];
  if (Array.isArray(animation)) {
    type = animation[0];
    const conf = ((_a = animation[1]) == null ? void 0 : _a[key]) ?? ((_b = animation[1]) == null ? void 0 : _b[shortKey]);
    if (conf) {
      if (typeof conf === "string") {
        type = conf;
      } else {
        type = conf.type || type;
        extraConf = conf;
      }
    }
  } else {
    const val = (animation == null ? void 0 : animation[key]) ?? (animation == null ? void 0 : animation[shortKey]);
    type = val == null ? void 0 : val.type;
    extraConf = val;
  }
  const found = animations[type];
  if (!found) {
    throw new Error(`No animation of type "${type}" for key "${key}"`);
  }
  return {
    ...found,
    ...extraConf
  };
}
const transformShorthands = {
  x: "translateX",
  y: "translateY",
  translateX: "x",
  translateY: "y"
};
function getValue(input, isColor = false) {
  if (typeof input !== "string") {
    return [input];
  }
  const [_, number, after] = input.match(/([-0-9]+)(deg|%|px)/) ?? [];
  return [+number, after];
}
export {
  AnimatedText,
  AnimatedView,
  createAnimations,
  useAnimatedNumber,
  useAnimatedNumberReaction,
  useAnimatedNumberStyle
};
//# sourceMappingURL=createAnimations.js.map
